// P-07 product page: arrow-key gallery navigation + phone sticky purchase bar.
// Reuses Dawn's own thumbnail/slider buttons and product form; no network calls, no dependencies.
// Dawn's HTMLUpdateUtility re-runs this script whenever it swaps the product section, so each run owns the root
// that contains its <script> tag and first tears down everything the previous run created (body bar, observers).
(() => {
  const root = document.currentScript?.closest('.cj-pdp') || document.querySelector('.cj-pdp');
  if (!root || root.dataset.cjPdpReady) return;
  window.cjPdpTeardown?.();
  root.dataset.cjPdpReady = 'true';

  const cleanup = [];
  const teardown = () => {
    cleanup.splice(0).forEach((fn) => fn());
    if (window.cjPdpTeardown === teardown) window.cjPdpTeardown = null;
  };
  window.cjPdpTeardown = teardown;
  const events = new AbortController();
  const signal = events.signal;
  cleanup.push(() => events.abort());
  // Theme editor: removing or reloading the section must not leave the body-mounted bar behind.
  document.addEventListener('shopify:section:unload', (event) => event.target.contains(root) && teardown(), { signal });

  // ---- Gallery: Left/Right step through media, never stealing keys from form fields or media controls.
  const gallery = root.querySelector('media-gallery');
  const nativeKeys = 'input, textarea, select, [contenteditable], video, audio, iframe, model-viewer, deferred-media, product-model';
  gallery?.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (event.target.closest(nativeKeys)) return;
    const step = event.key === 'ArrowRight' ? 1 : -1;
    const thumbs = [...gallery.querySelectorAll('[id^="GalleryThumbnails"] .thumbnail')].filter((b) => b.offsetParent);
    if (thumbs.length > 1) {
      const current = Math.max(0, thumbs.findIndex((b) => b.hasAttribute('aria-current')));
      const target = thumbs[(current + step + thumbs.length) % thumbs.length];
      target.click();
      target.focus();
      event.preventDefault();
      return;
    }
    const button = gallery.querySelector(`[id^="GalleryViewer"] .slider-button--${step > 0 ? 'next' : 'prev'}`);
    if (button && button.offsetParent && !button.disabled) {
      button.click();
      event.preventDefault();
    }
  }, { signal });
  // Dawn disables Previous/Next at either end; a focused button that becomes disabled drops focus to <body>.
  // Hand focus to the other arrow instead.
  const arrows = gallery ? [...gallery.querySelectorAll('[id^="GalleryViewer"] .slider-button')] : [];
  let lastFocused = null;
  gallery?.addEventListener('focusin', (event) => { lastFocused = event.target; }, { signal });
  if (arrows.length) {
    const arrowWatch = new MutationObserver((records) => records.forEach(({ target }) => {
      const lost = document.activeElement === target || document.activeElement === document.body || !document.activeElement;
      if (!target.disabled || lastFocused !== target || !lost) return;
      arrows.find((b) => b !== target && !b.disabled && b.offsetParent)?.focus();
    }));
    arrows.forEach((b) => arrowWatch.observe(b, { attributes: true, attributeFilter: ['disabled'] }));
    cleanup.push(() => arrowWatch.disconnect());
  }

  // ---- Sticky purchase bar (phones).
  const bar = root.querySelector('[data-cj-pdp-bar]');
  const submit = root.querySelector('.product-form__submit');
  // A product that cannot be bought anchors on the consultation block, never on its disabled buttons.
  const anchor = submit && !submit.disabled
    ? root.querySelector('.product-form__buttons')
    : root.querySelector('[data-cj-consult]');
  if (!bar || !anchor || !('IntersectionObserver' in window)) return;
  // Dawn's slide-in reveal transforms the info column, which would trap position: fixed; so the bar lives on <body>.
  document.body.append(bar);
  bar.hidden = false;
  cleanup.push(() => {
    bar.remove();
    document.body.style.removeProperty('--cj-pdp-bar-h');
  });
  // Publish the bar's real height (it grows with the safe-area inset) so floating widgets can clear it.
  const setHeight = () => document.body.style.setProperty('--cj-pdp-bar-h', `${bar.offsetHeight}px`);
  setHeight();
  if ('ResizeObserver' in window) {
    const resize = new ResizeObserver(setHeight);
    resize.observe(bar);
    cleanup.push(() => resize.disconnect());
  }
  // Show only once the anchor has scrolled up out of view; hide again when it returns. The huge bottom margin
  // makes "below the viewport" count as intersecting, so a jump straight past the anchor still fires.
  const visibility = new IntersectionObserver(([entry]) => {
    if (!root.isConnected) return teardown();
    bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
  }, { rootMargin: '0px 0px 100000px 0px' });
  visibility.observe(anchor);
  cleanup.push(() => visibility.disconnect());

  // The sticky button only forwards to Dawn's native button and mirrors its loading/disabled state, so Dawn's
  // cart logic and error semantics stay authoritative.
  const add = bar.querySelector('[data-cj-pdp-add]');
  if (!add || !submit) return;
  const error = root.querySelector('.product-form__error-message-wrapper');
  const busy = () => submit.classList.contains('loading');
  const blocked = () => submit.disabled || submit.getAttribute('aria-disabled') === 'true';
  let fromBar = false;
  const sync = () => {
    add.setAttribute('aria-disabled', String(blocked()));
    add.setAttribute('aria-busy', String(busy()));
    if (!fromBar || busy()) return;
    fromBar = false;
    // A rejected add (e.g. 422) renders Dawn's role="alert" message beside the native button: bring the customer
    // there and put focus on the native button, since this bar hides once that area is in view.
    if (error && !error.hidden) {
      error.scrollIntoView({ block: 'center' });
      submit.focus({ preventScroll: true });
    }
  };
  const mirror = new MutationObserver(sync);
  mirror.observe(submit, { attributes: true, attributeFilter: ['class', 'aria-disabled', 'disabled'] });
  if (error) mirror.observe(error, { attributes: true, attributeFilter: ['hidden'] });
  cleanup.push(() => mirror.disconnect());
  sync();
  add.addEventListener('click', () => {
    if (busy()) return; // never queue a second submission while Dawn's request is in flight
    if (blocked()) {
      anchor.scrollIntoView({ block: 'center' });
      return;
    }
    fromBar = true;
    submit.click();
  }, { signal });
})();
